/*********************************************************************************
 * Copyright (C) 2025 VECROS Technologies Pvt. Ltd.
 * <legal@vecros.com>
 * This file is part of VECROS CLOUD SOFTWARE SERVICES maintained and developed by VECROS TECHNOLOGIES PRIVATE LIMITED.
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 **********************************************************************************/

import { useEffect, useState } from "react";
import { Box, InputBase, Skeleton, Typography, useTheme } from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ArrowUpwardRoundedIcon from "@mui/icons-material/ArrowUpwardRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useAssistant } from "./apps/drive/assistant";
import { AI_GRADIENT } from "./apps/drive/assistant/model/tokens";
import * as drive from "../api/drive";
import { formatBytes, formatDate } from "./apps/drive/format";

const greeting = () => {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
};

const SUGGESTIONS = [
  "What did I upload this week?",
  "Find my largest files",
  "Which files have I shared publicly?",
  "Summarise what's in my Drive",
];

const CARDS = [
  {
    tag: "Files",
    title: "Find something fast",
    body: "Describe what you're looking for instead of clicking through folders.",
    seed: "Help me find a file — I'll describe it.",
  },
  {
    tag: "Organise",
    title: "Tidy up your Drive",
    body: "Spot duplicates, stray files outside folders, and things worth archiving.",
    seed: "What in my Drive could use tidying up?",
  },
  {
    tag: "Sharing",
    title: "Review what's shared",
    body: "See which files are reachable by link and revoke the ones that shouldn't be.",
    seed: "Which of my files are shared publicly right now?",
  },
];

/**
 * Overview / home — the AI launchpad, matching the accounts Overview. The stats
 * strip and recent list are drive-specific: accounts' version is purely
 * assistant entry points, but drive has real content worth surfacing here.
 */
export default function Overview() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { openPanel, send } = useAssistant();
  const [text, setText] = useState("");
  const [stats, setStats] = useState(null);

  const name = user?.given_name || user?.name?.split(" ")[0];

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        // A flat page-1 sample: enough for headline numbers and a recent list
        // without walking the whole tree. `count` is the true total.
        const [files, folders, shares] = await Promise.all([
          drive.listFiles({ pageSize: 100 }),
          drive.listFolders({ pageSize: 100 }),
          drive.listShares({ pageSize: 100 }).catch(() => ({ owned: [] })),
        ]);
        if (!active) return;
        const recent = [...files.files]
          .sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at))
          .slice(0, 5);
        setStats({
          fileCount: files.count,
          folderCount: folders.count,
          shareCount: (shares.owned || []).length,
          bytes: files.files.reduce((sum, f) => sum + (f.size_bytes || 0), 0),
          sampled: files.files.length < files.count,
          recent,
        });
      } catch {
        if (active) setStats({ error: true });
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const ask = (prompt) => {
    const t = (prompt || "").trim();
    if (!t) return;
    openPanel();
    send(t);
    setText("");
  };

  const cardSx = {
    backgroundColor: theme.palette.secondary.main,
    border: `1px solid ${theme.palette.border.color}`,
    borderRadius: "14px",
  };

  const STATS = stats && !stats.error
    ? [
        { label: "Files", value: stats.fileCount },
        { label: "Folders", value: stats.folderCount },
        { label: "Shared links", value: stats.shareCount },
        // Marked approximate when the sample didn't cover every file.
        { label: "Storage used", value: `${stats.sampled ? "≥ " : ""}${formatBytes(stats.bytes)}` },
      ]
    : null;

  return (
    <Box
      sx={{
        boxSizing: "border-box",
        backgroundColor: theme.palette.primary.main,
        minHeight: "100%",
        p: { xs: 2, md: 4 },
        color: theme.palette.text.color,
        overflow: "auto",
        scrollbarWidth: "none",
      }}
    >
      <Box sx={{ maxWidth: 760, mx: "auto", pt: { xs: 2, md: 6 } }}>
        <Box sx={{ textAlign: "center" }}>
          <Box
            aria-hidden
            sx={{
              width: 56,
              height: 56,
              borderRadius: "16px",
              display: "inline-grid",
              placeItems: "center",
              background: AI_GRADIENT,
              color: "#fff",
              mb: 2,
            }}
          >
            <AutoAwesomeIcon sx={{ fontSize: 28 }} />
          </Box>
          <Typography sx={{ color: theme.palette.secondaryText?.color, fontSize: 16 }}>
            {greeting()}
            {name ? `, ${name}` : ""}
          </Typography>
          <Typography sx={{ fontSize: 28, fontWeight: 600, letterSpacing: "-0.02em" }}>
            How can I help?
          </Typography>

          <Box
            sx={{
              mt: 3,
              display: "flex",
              alignItems: "center",
              gap: 1,
              maxWidth: 620,
              mx: "auto",
              px: 1.5,
              py: 1,
              border: `1px solid ${theme.palette.border.color}`,
              borderRadius: "14px",
              backgroundColor: theme.palette.secondary.main,
              transition: "border-color 0.15s ease",
              "&:focus-within": { borderColor: "accent.main" },
            }}
          >
            <InputBase
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") ask(text);
              }}
              placeholder="Ask anything about your files and folders…"
              sx={{ flex: 1, fontSize: 15, color: theme.palette.text.color }}
              inputProps={{ "aria-label": "Ask the assistant" }}
            />
            <Box
              role="button"
              aria-label="Ask"
              onClick={() => ask(text)}
              sx={{
                display: "grid",
                placeItems: "center",
                width: 34,
                height: 34,
                borderRadius: "10px",
                cursor: text.trim() ? "pointer" : "default",
                backgroundColor: text.trim() ? "accent.solid" : theme.palette.border.color,
                color: "#fff",
              }}
            >
              <ArrowUpwardRoundedIcon sx={{ fontSize: 20 }} />
            </Box>
          </Box>

          <Box sx={{ mt: 2, display: "flex", flexWrap: "wrap", gap: 1, justifyContent: "center" }}>
            {SUGGESTIONS.map((s) => (
              <Box
                key={s}
                role="button"
                tabIndex={0}
                onClick={() => ask(s)}
                onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && ask(s)}
                sx={{
                  fontSize: 13,
                  px: 1.5,
                  py: 0.75,
                  borderRadius: "999px",
                  cursor: "pointer",
                  border: `1px solid ${theme.palette.border.color}`,
                  color: theme.palette.text.color,
                  "&:hover": { borderColor: "accent.main" },
                }}
              >
                {s}
              </Box>
            ))}
          </Box>
        </Box>

        {/* Drive at a glance */}
        <Box
          sx={{
            mt: 5,
            display: "grid",
            gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(4, 1fr)" },
            gap: 2,
          }}
        >
          {(STATS || Array.from({ length: 4 })).map((stat, index) => (
            <Box key={stat?.label || index} sx={{ ...cardSx, p: 2 }}>
              {stat ? (
                <>
                  <Typography sx={{ fontSize: 22, fontWeight: 600 }}>{stat.value}</Typography>
                  <Typography sx={{ fontSize: 12.5, color: theme.palette.secondaryText?.color }}>
                    {stat.label}
                  </Typography>
                </>
              ) : (
                <>
                  <Skeleton width={60} height={30} />
                  <Skeleton width={80} height={18} />
                </>
              )}
            </Box>
          ))}
        </Box>

        {/* Recent files */}
        {stats?.recent?.length > 0 && (
          <Box sx={{ ...cardSx, mt: 2 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 600, px: 2, pt: 2, pb: 1 }}>
              Recent files
            </Typography>
            {stats.recent.map((file) => (
              <Box
                key={file.id}
                role="button"
                tabIndex={0}
                onClick={() =>
                  navigate(file.folder_id ? `/drive/folders/${file.folder_id}` : "/drive")
                }
                onKeyDown={(e) =>
                  (e.key === "Enter" || e.key === " ") &&
                  navigate(file.folder_id ? `/drive/folders/${file.folder_id}` : "/drive")
                }
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.25,
                  px: 2,
                  py: 1.25,
                  cursor: "pointer",
                  borderTop: `1px solid ${theme.palette.border.color}`,
                  "&:hover": { backgroundColor: theme.palette.tertiary.main },
                }}
              >
                <InsertDriveFileOutlinedIcon
                  sx={{ fontSize: 18, color: theme.palette.secondaryText?.color }}
                />
                <Typography sx={{ fontSize: 13.5, flex: 1 }} noWrap>
                  {file.file_name}
                </Typography>
                <Typography sx={{ fontSize: 12, color: theme.palette.secondaryText?.color }}>
                  {formatBytes(file.size_bytes)}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 12,
                    color: theme.palette.secondaryText?.color,
                    minWidth: 90,
                    textAlign: "right",
                  }}
                >
                  {formatDate(file.updated_at)}
                </Typography>
              </Box>
            ))}
          </Box>
        )}

        {/* Assistant entry cards */}
        <Box
          sx={{
            mt: 4,
            mb: 4,
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" },
            gap: 2,
          }}
        >
          {CARDS.map((c) => (
            <Box
              key={c.title}
              role="button"
              tabIndex={0}
              onClick={() => ask(c.seed)}
              onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && ask(c.seed)}
              sx={{
                ...cardSx,
                p: 2,
                cursor: "pointer",
                display: "flex",
                flexDirection: "column",
                transition: "transform 0.15s ease, border-color 0.15s ease",
                "&:hover": { transform: "translateY(-2px)", borderColor: "accent.main" },
              }}
            >
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 600,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  color: "accent.main",
                  mb: 1,
                }}
              >
                {c.tag}
              </Typography>
              <Typography sx={{ fontSize: 16, fontWeight: 600, mb: 0.5 }}>{c.title}</Typography>
              <Typography
                sx={{
                  fontSize: 13,
                  color: theme.palette.secondaryText?.color,
                  lineHeight: 1.5,
                  flex: 1,
                }}
              >
                {c.body}
              </Typography>
              <Box
                sx={{
                  mt: 1.5,
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  fontSize: 13,
                  fontWeight: 600,
                  color: "accent.main",
                }}
              >
                Ask the assistant
                <ArrowForwardRoundedIcon sx={{ fontSize: 16 }} />
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}
